import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getActiveSeason } from '../config/seasons';
import * as schoolService from '../services/schoolService';

/**
 * The teacher's school and its seasons — loaded once for the whole school
 * portal (<SchoolProvider> in SchoolRegistration) and shared by every step.
 * Each page used to load its own copy, so creating the season on Setup didn't
 * reach the router's copy and Students bounced straight back to Setup.
 *
 * `school` is permanent (name, city, contact). `currentSeason` is the row for
 * whichever year is active in seasons.js — null when the school exists but has
 * not signed up for this year yet, which is exactly the state a returning
 * teacher lands in. That distinction is the whole point of the split.
 */
const useSchoolState = () => {
  const { user } = useAuth();
  const [school, setSchool] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const activeYear = getActiveSeason().year;
  const currentSeason = seasons.find(s => s.competition_year === activeYear) || null;

  // quiet: refresh in place (after finalize / reopen) without the portal's
  // full-page loader, which would unmount the step that asked for it.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!user) return;
    try {
      if (!quiet) setLoading(true);
      setError(null);
      const mine = await schoolService.getMySchool(user.id);
      setSchool(mine);
      setSeasons(mine ? await schoolService.getSchoolSeasons(mine.id) : []);
    } catch (err) {
      console.error('❌ Error loading school:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) load();
    else setLoading(false);
  }, [user, load]);

  /**
   * Save the school's details, creating it on first use. Never touches the
   * season — a teacher editing their address in March shouldn't re-sign a
   * waiver, and a returning teacher shouldn't retype their address to sign one.
   */
  const saveSchoolProfile = useCallback(async (profileData) => {
    if (!user) return false;
    try {
      setSaving(true);
      setError(null);
      const saved = school
        ? await schoolService.updateSchool(school.id, profileData)
        : await schoolService.createSchool(profileData);
      setSchool(prev => ({ ...saved, my_role: prev?.my_role || 'owner' }));
      return saved;
    } catch (err) {
      console.error('❌ Error saving school:', err);
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [user, school]);

  /** Sign (or re-sign) the waiver for the active season, opening that season. */
  const signWaiver = useCallback(async (waiver, schoolId = school?.id) => {
    if (!schoolId) return false;
    try {
      setSaving(true);
      setError(null);
      const saved = await schoolService.signSeasonWaiver(schoolId, activeYear, waiver);
      setSeasons(prev => [saved, ...prev.filter(s => s.id !== saved.id)]);
      return saved;
    } catch (err) {
      console.error('❌ Error signing waiver:', err);
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [school, activeYear]);

  const updateSeasonState = (saved) =>
    setSeasons(prev => prev.map(s => (s.id === saved.id ? saved : s)));

  const reopenSeason = useCallback(async () => {
    if (!currentSeason?.id) return false;
    try {
      setSaving(true);
      updateSeasonState(await schoolService.reopenSeason(currentSeason.id));
      return true;
    } catch (err) {
      console.error('❌ Error reopening season:', err);
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [currentSeason]);

  return {
    school,
    seasons,
    currentSeason,
    activeYear,
    loading,
    error,
    saving,
    saveSchoolProfile,
    signWaiver,
    reopenSeason,
    reload: () => load({ quiet: true }),
    hasSchool: !!school,
    hasCurrentSeason: !!currentSeason,
    isPendingReview: school?.status === 'pending_review',
    isRejected: school?.status === 'rejected',
    isFinalized: !!currentSeason?.is_finalized,
  };
};

const SchoolContext = createContext(null);

export const SchoolProvider = ({ children }) => {
  const value = useSchoolState();
  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
};

const useSchool = () => {
  const ctx = useContext(SchoolContext);
  if (!ctx) throw new Error('useSchool() must be used inside <SchoolProvider> (the school portal).');
  return ctx;
};

export default useSchool;
